"use client";

import { SkipLink } from "@hisani/ui";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { useTranslations } from "next-intl";
import React, { useEffect, type ReactNode } from "react";
import hisaniLogo from "../../../../brand/hisani_logo.png";
import { AccountAction, MobileAccountLinks } from "./account-action";
import { useAuth } from "./auth-client";
import { HeaderNavigation, HeaderNavigationLink } from "./header-navigation";
import { MobileNavigation } from "./mobile-navigation";
import { PlatformBottomNav } from "./platform-bottom-nav";
import { PlatformMobileHeader } from "./platform-mobile-header";
import { PlatformSidebar } from "./platform-sidebar";
import { SiteFooter } from "./[locale]/site-footer";
import { SiteFooterVisibility } from "./[locale]/site-footer-visibility";
import {
  dashboardServiceFromPathname,
  recordDashboardServiceVisit,
} from "./[locale]/dashboard/dashboard-personalization";

export function isMarketingPath(pathname: string, locale: string): boolean {
  const normalizedPath = pathname.replace(/\/+$/u, "") || "/";
  const strippedPath = normalizedPath.startsWith(`/${locale}`)
    ? normalizedPath.slice(locale.length + 1) || "/"
    : normalizedPath;

  return (
    strippedPath === "/" ||
    strippedPath === "/pricing" ||
    strippedPath === "/privacy" ||
    strippedPath === "/terms" ||
    strippedPath.startsWith("/products")
  );
}

export function isFocusedFlowPath(pathname: string, locale: string): boolean {
  const normalizedPath = pathname.replace(/\/+$/u, "") || "/";
  const strippedPath = normalizedPath.startsWith(`/${locale}`)
    ? normalizedPath.slice(locale.length + 1) || "/"
    : normalizedPath;
  return strippedPath === "/marketplace/create";
}

function isFixedHeightWorkspacePath(pathname: string, locale: string): boolean {
  const normalizedPath = pathname.replace(/\/+$/u, "") || "/";
  const strippedPath = normalizedPath.startsWith(`/${locale}`)
    ? normalizedPath.slice(locale.length + 1) || "/"
    : normalizedPath;
  return strippedPath === "/hire/conversation";
}

export function AppShell({
  children,
  locale,
}: {
  readonly children: ReactNode;
  readonly locale: string;
}) {
  const auth = useAuth();
  const pathname = usePathname();
  const t = useTranslations("Common");

  const isMarketing = isMarketingPath(pathname, locale);
  const isFocusedFlow = isFocusedFlowPath(pathname, locale);
  const isFixedHeightWorkspace = isFixedHeightWorkspacePath(pathname, locale);
  const showPlatformShell = !isMarketing && auth.status !== "anonymous";

  useEffect(() => {
    if (auth.status !== "authenticated") return;
    const service = dashboardServiceFromPathname(pathname, locale);
    if (service === null) return;
    recordDashboardServiceVisit(
      window.localStorage,
      auth.account.id,
      service,
      Date.now(),
    );
  }, [auth, locale, pathname]);

  const marketingNavigation = [
    { href: "/products/stopwatch" as const, label: t("stopwatch") },
    { href: "/products/planner" as const, label: t("planner") },
    { href: "/products/marketplace" as const, label: t("marketplace") },
    { href: "/product/stable" as const, label: t("stable") },
    { href: "/pricing" as const, label: t("pricing") },
  ];

  if (!showPlatformShell) {
    return (
      <div className="min-h-screen">
        <SkipLink href="#main-content">{t("skipToContent")}</SkipLink>
        <div className="grid min-h-dvh grid-cols-1 grid-rows-[auto_1fr] pt-[calc(var(--mobile-app-header-height)+env(safe-area-inset-top,0px))] md:pt-0">
          <HeaderNavigation className="site-header mobile-app-header fixed inset-x-0 top-0 border-b border-brand bg-logo md:sticky md:inset-x-auto md:h-[70px] md:pt-0">
            <div className="mx-auto flex h-full w-full max-w-app items-center justify-between gap-6 px-4 sm:px-6 lg:px-8">
              <HeaderNavigationLink
                href="/"
                id="header-logo-link"
                className="inline-flex min-h-11 items-center rounded-xs bg-logo px-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
              >
                <Image
                  src={hisaniLogo}
                  alt={t("title")}
                  width={153}
                  height={75}
                  priority
                  className="h-11 w-auto"
                />
              </HeaderNavigationLink>

              <nav
                aria-label={t("primaryNavigation")}
                className="hidden items-center gap-8 md:flex"
              >
                {marketingNavigation.map((item) => (
                  <HeaderNavigationLink
                    key={item.href}
                    href={item.href}
                    className="inline-flex min-h-11 items-center text-sm font-semibold text-primary transition-colors hover:text-brand focus-visible:rounded-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
                  >
                    {item.label}
                  </HeaderNavigationLink>
                ))}
              </nav>

              <div className="flex items-center gap-3">
                <AccountAction />

                <MobileNavigation
                  openLabel={t("menu")}
                  closeLabel={t("closeMenu")}
                  navigationLabel={t("mobileNavigation")}
                >
                  {marketingNavigation.map((item) => (
                    <HeaderNavigationLink
                      key={item.href}
                      href={item.href}
                      className="flex min-h-11 items-center rounded-sm px-3 text-sm font-semibold text-primary hover:bg-subtle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
                    >
                      {item.label}
                    </HeaderNavigationLink>
                  ))}
                  <MobileAccountLinks />
                </MobileNavigation>
              </div>
            </div>
          </HeaderNavigation>

          <main
            id="main-content"
            tabIndex={-1}
            className="flex min-h-0 flex-col outline-none"
          >
            {children}
          </main>
        </div>

        <SiteFooterVisibility locale={locale}>
          <SiteFooter />
        </SiteFooterVisibility>
      </div>
    );
  }

  // Authenticated Platform Layout
  return (
    <div
      data-platform-shell=""
      data-hisani-dialog-portal=""
      className="relative flex h-lvh min-h-0 overflow-hidden bg-canvas md:h-auto md:min-h-screen md:overflow-visible"
    >
      <SkipLink href="#main-content">{t("skipToContent")}</SkipLink>
      {/* Desktop Left Navigation Sidebar */}
      <PlatformSidebar />

      {/* Main Content Area */}
      <div
        data-platform-shell-content=""
        className={`flex min-h-0 min-w-0 flex-1 flex-col ${
          isFocusedFlow
            ? ""
            : "pt-[calc(var(--mobile-app-header-height)+env(safe-area-inset-top,0px))] pb-[calc(var(--mobile-bottom-navigation-height)+var(--mobile-bottom-navigation-bottom-spacing)+1px)] md:py-0"
        }`}
      >
        {/* Mobile Top Header */}
        {isFocusedFlow ? null : <PlatformMobileHeader />}

        {/* Workspace Page Content */}
        <main
          id="main-content"
          tabIndex={-1}
          className={`mobile-app-scroll flex min-h-0 flex-1 flex-col outline-none md:overflow-visible ${
            isFixedHeightWorkspace ? "overflow-hidden" : "overflow-y-auto"
          }`}
        >
          {children}
        </main>

        {/* Mobile Bottom Navigation Bar */}
        {isFocusedFlow ? null : <PlatformBottomNav />}
      </div>
    </div>
  );
}
